/**
 * InlineReferenceSuggest
 * ──────────────────────
 * Auto-suggest dropdown that watches a textarea and surfaces matching
 * lesson titles from the local library when the user types `@` (mentions
 * a lesson title) or starts a query.
 *
 * Usage:
 *   const ref = useRef(null);
 *   <textarea ref={ref} value={msg} onChange={…} />
 *   <InlineReferenceSuggest
 *     textareaRef={ref}
 *     value={msg}
 *     setValue={setMsg}
 *     library={libraryLessons}        // [{ id, title, subject? }]
 *     onAddChip={ref => setRefs([...refs, ref])}
 *   />
 *
 * Triggers when the user types `@` followed by ≥2 chars (or `[[`). When a
 * lesson is chosen, the trigger text is replaced and a reference chip is
 * appended via `onAddChip`. Keyboard: ↑/↓ to navigate, Enter to confirm,
 * Esc to dismiss.
 *
 * Lazy: only renders when `library.length > 0` and trigger is active.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { BookMarked } from 'lucide-react';

// Find the most-recent @trigger token before the caret.
function detectTrigger(value, caret) {
  if (!value || caret == null) return null;
  const head = value.slice(0, caret);
  // Match: whitespace OR start, then '@', then 0+ chars excluding whitespace.
  const m = /(^|\s)@([^\s@]{0,40})$/.exec(head);
  if (!m) return null;
  return { query: m[2], start: caret - m[2].length - 1, end: caret };
}

function rankLibrary(library, q, maxItems) {
  const qn = (q || '').toLowerCase();
  if (!qn) return library.slice(0, maxItems);
  return library
    .map(item => {
      const t = (item.title || '').toLowerCase();
      const i = t.indexOf(qn);
      const score = i === -1 ? 1000 : (i === 0 ? -50 : i);
      return { item, score };
    })
    .filter(r => r.score < 1000)
    .sort((a, b) => a.score - b.score)
    .slice(0, maxItems)
    .map(r => r.item);
}

export default function InlineReferenceSuggest({
  textareaRef,
  value,
  setValue,
  library = [],
  onAddChip,
  testIdPrefix = 'ref-suggest',
  minQueryLength = 1,       // 0 → bare '@' opens the full list
  maxItems = 6,
  headerLabel = 'Suggestions',
  onPick = null,            // (item) => void — overrides the default lesson-chip payload
}) {
  const [trigger, setTrigger] = useState(null);
  const [selectedIdx, setSelectedIdx] = useState(0);
  const containerRef = useRef(null);

  // Recompute trigger on every change.
  useEffect(() => {
    const ta = textareaRef?.current;
    if (!ta) { setTrigger(null); return; }
    const t = detectTrigger(value, ta.selectionStart);
    setTrigger(t);
    setSelectedIdx(0);
  }, [value, textareaRef]);

  const candidates = useMemo(() => {
    if (!trigger || trigger.query.length < minQueryLength) return [];
    return rankLibrary(library, trigger.query, maxItems);
  }, [trigger, library, minQueryLength, maxItems]);

  // Keyboard navigation
  useEffect(() => {
    if (!trigger || candidates.length === 0) return;
    const ta = textareaRef?.current;
    if (!ta) return;
    const handler = (e) => {
      if (e.key === 'ArrowDown') { e.preventDefault(); setSelectedIdx(i => (i + 1) % candidates.length); }
      else if (e.key === 'ArrowUp') { e.preventDefault(); setSelectedIdx(i => (i - 1 + candidates.length) % candidates.length); }
      else if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault();
        confirm(candidates[selectedIdx]);
      } else if (e.key === 'Escape') {
        setTrigger(null);
      }
    };
    ta.addEventListener('keydown', handler);
    return () => ta.removeEventListener('keydown', handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trigger, candidates, selectedIdx]);

  // Long lists scroll (max-h-64) — keep the arrow-key highlight visible.
  useEffect(() => {
    const row = containerRef.current?.querySelector(`[data-testid="${testIdPrefix}-item-${selectedIdx}"]`);
    row?.scrollIntoView?.({ block: 'nearest' });
  }, [selectedIdx, testIdPrefix]);

  const confirm = (item) => {
    if (!item || !trigger) return;
    // Default: replace the @query with the lesson title (so the message reads
    // naturally) and add the lesson as a reference chip behind the scenes.
    // With `onPick` the caller shows its own chip/tag for the item, so the
    // @query is just removed — the title must not appear twice.
    const before = value.slice(0, trigger.start);
    const after = value.slice(trigger.end);
    const titleEsc = (item.title || 'lesson').slice(0, 80);
    const inserted = onPick ? '' : `"${titleEsc}"`;
    const next = `${before}${inserted}${after}`;
    setValue(next);
    if (onPick) {
      onPick(item);
    } else if (onAddChip) {
      onAddChip({
        id: `ref_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`,
        kind: 'lesson',
        label: titleEsc,
        url: item.url || `lesson://${item.id}`,
        lesson_id: item.id,
      });
    }
    setTrigger(null);
    // Restore caret position just after the inserted title.
    setTimeout(() => {
      const ta = textareaRef?.current;
      if (!ta) return;
      const pos = before.length + inserted.length;
      ta.focus();
      ta.setSelectionRange(pos, pos);
    }, 0);
  };

  if (!trigger || candidates.length === 0) return null;

  return (
    <div
      ref={containerRef}
      data-testid={`${testIdPrefix}-dropdown`}
      className="absolute z-[99] bottom-full mb-1 left-0 right-auto rounded-xl border shadow-2xl overflow-hidden"
      style={{
        background: 'var(--st-card, #fff)',
        borderColor: 'var(--st-border, #e5e7eb)',
        minWidth: 280,
        maxWidth: 'min(440px, 90vw)',
      }}>
      <div className="px-3 py-2 text-[10px] uppercase tracking-widest font-bold opacity-60 flex items-center gap-1.5 border-b" style={{ borderColor: 'var(--st-border, #e5e7eb)' }}>
        <BookMarked className="w-3 h-3" />
        {headerLabel} · {candidates.length}
      </div>
      <ul className="max-h-64 overflow-auto">
        {candidates.map((c, i) => (
          <li key={`${c.id}_${i}`}>
            <button
              type="button"
              onMouseEnter={() => setSelectedIdx(i)}
              onClick={() => confirm(c)}
              data-testid={`${testIdPrefix}-item-${i}`}
              className="w-full px-3 py-2 text-left text-[12px] flex items-center gap-2 transition-colors"
              style={{
                background: i === selectedIdx ? 'color-mix(in srgb, #0EA5E9 14%, transparent)' : 'transparent',
              }}>
              <BookMarked className="w-3.5 h-3.5 opacity-60 shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="font-bold truncate" style={{ color: 'var(--st-ink, #0f172a)' }}>{c.title}</div>
                {c.subject ? <div className="text-[10px] opacity-60 truncate">{c.subject}</div> : null}
              </div>
              {i === selectedIdx && <span className="text-[9px] opacity-60 font-bold">↵</span>}
            </button>
          </li>
        ))}
      </ul>
      <div className="px-3 py-1.5 text-[9px] opacity-50 border-t" style={{ borderColor: 'var(--st-border, #e5e7eb)' }}>
        ↑↓ navigate · ↵ insert · Esc cancel
      </div>
    </div>
  );
}
